import type { ComponentProps, ExternalLinkCustomProps } from './types'

const LINK_CLASSES = 'inline-block text-primary-600 cursor-pointer hover:text-primary-800 hover:underline text-base'

interface FormsExternalLinkProps extends ComponentProps {
	// Admin builder/preview surfaces use isPreview so an unconfigured component
	// shows a helpful hint. Participant-facing surfaces leave it false (default)
	// so an unconfigured external link renders nothing instead of leaking the
	// admin-only "Configure URL or upload a document" string.
	isPreview?: boolean
}

export const FormsExternalLink = ({ component, isPreview = false }: FormsExternalLinkProps) => {
	const { url, linkText, document: docValue } = component.custom as ExternalLinkCustomProps

	const href = docValue?.url || url
	const isDocumentLink = Boolean(docValue?.url)

	if (href && linkText) {
		// NOTE: the OS save-dialog filename and "force download instead of open
		// in tab" behavior are driven by the `Content-Disposition` header set on
		// the S3 object in `organizations.service.uploadLogo`, NOT by the HTML
		// `download` attribute. Browsers silently ignore `download` on
		// cross-origin URLs, and the CDN host differs from the app host.
		// A11y (WCAG 2.1 SC 3.2.2): document anchors still get `target='_blank'`,
		// so the browser opens a tab/context before the Content-Disposition kicks
		// in. Announce `(downloads file)` to screen readers so focus-leaves-tab
		// behaviour is not silent.
		return (
			<a href={href} target='_blank' rel='noopener noreferrer' className={LINK_CLASSES}>
				{linkText}
				<span className='sr-only'>{isDocumentLink ? ' (downloads file)' : ' (opens in new tab)'}</span>
			</a>
		)
	}

	if (!isPreview) {
		return null
	}

	return <span className='text-neutral-400 text-base'>Configure URL or upload a document</span>
}
